export type SearchableValue = string | number | null | undefined

/**
 * Normalizes user-facing labels for deterministic, accent-insensitive search.
 * NFKC also converts compatibility characters such as full-width digits.
 */
export const normalizeSearchText = (value: SearchableValue): string =>
  String(value ?? '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()

const compactSearchText = (value: string): string => value.replace(/[^\p{L}\p{N}]+/gu, '')

/**
 * Matches a query against explicitly provided, user-facing values.
 * Besides a normalized substring match, a compact comparison makes visual
 * separators irrelevant (for example, "1131" matches "11 31" or "11-31").
 */
export const matchesSearchQuery = (query: SearchableValue, values: SearchableValue | SearchableValue[]): boolean => {
  const normalizedQuery = normalizeSearchText(query)
  if (!normalizedQuery) return true

  const compactQuery = compactSearchText(normalizedQuery)
  const searchableValues = Array.isArray(values) ? values : [values]
  const normalizedValues = searchableValues.map(normalizeSearchText)
  const candidates = normalizedValues.length > 1 ? [...normalizedValues, normalizedValues.join(' ')] : normalizedValues

  return candidates.some(normalizedValue => {
    if (normalizedValue.includes(normalizedQuery)) return true

    return compactQuery.length > 0 && compactSearchText(normalizedValue).includes(compactQuery)
  })
}
